import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DATE_TIME_FORMAT, formatDateSafe } from '../../../utils/dateUtils';
import type { DocumentVersion } from '@/types/documents';

interface CompareVersionsDialogProps {
  open: boolean;
  from: DocumentVersion | null;
  to: DocumentVersion | null;
  onOpenChange: (open: boolean) => void;
}

const CompareVersionsDialog: React.FC<CompareVersionsDialogProps> = ({
  open,
  from,
  to,
  onOpenChange,
}) => {
  const { t, i18n } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* No description to point at, and Radix warns unless that is explicit. */}
      <DialogContent className="revy-ui sm:max-w-[900px]" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="revy-cjk leading-[1.5]">
            {t('documents.compareVersions')}
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <p className="revy-cjk mb-2 text-base font-medium leading-[1.5]">
              {t('documents.versionWithNumber', { version: from?.versionNumber })}
            </p>
            <p
              title={from?.fileName}
              className="revy-cjk truncate text-sm leading-[1.5] text-muted-foreground"
            >
              {from?.fileName}
            </p>
            <p className="revy-figure text-sm text-muted-foreground">
              {from && formatDateSafe(from.uploadedAt, DATE_TIME_FORMAT, { t, language: i18n.language })}
            </p>
          </div>

          <span className="revy-cjk self-center text-lg leading-[1.5] text-muted-foreground">
            {t('documents.versus')}
          </span>

          <div className="min-w-0 flex-1 text-right">
            <p className="revy-cjk mb-2 text-base font-medium leading-[1.5]">
              {t('documents.versionWithCurrent', { version: to?.versionNumber })}
            </p>
            <p
              title={to?.fileName}
              className="revy-cjk truncate text-sm leading-[1.5] text-muted-foreground"
            >
              {to?.fileName}
            </p>
            <p className="revy-figure text-sm text-muted-foreground">
              {to && formatDateSafe(to.uploadedAt, DATE_TIME_FORMAT, { t, language: i18n.language })}
            </p>
          </div>
        </div>

        <div className="mt-2 rounded-md border border-dashed p-4 text-center">
          <p className="revy-cjk text-sm leading-[1.5] text-muted-foreground">
            {t('documents.versionComparisonComingSoon')}
          </p>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            <span className="revy-cjk">{t('common.close')}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CompareVersionsDialog;
